import React, { useEffect, useState } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';
import {
  SignupTermsBox,
  SignupStyledFormControlLabel,
  SignupStyledCheckbox,
  SignupTermsText,
  SignupTermHoverContainer,
  SignupStyledAllCheckbox,
  SignupTermsAllDivider,
  SignupTermsAllText,
} from './inquryStyles';
import InquiryTermsModal from './InquiryTermsModal';

const InquiryTerms = ({ control, errors, setValue }) => {
  const intl = useIntl();

  // 개별 약관 상태 감지
  const [termsReq1, termsReq2, termsOpt1, termsOpt2, termsAll] = useWatch({
    control,
    name: ['termsReq1', 'termsReq2', 'termsOpt1', 'termsOpt2', 'termsAll'],
  });

  // 단일 모달 상태 관리 (어떤 약관을 열었는지 alias로 구분)
  const [termsModalConfig, setTermsModalConfig] = useState({ isOpen: false, alias: '' });

  const openTermsModal = (alias) => setTermsModalConfig({ isOpen: true, alias });
  const closeTermsModal = () => setTermsModalConfig({ isOpen: false, alias: '' });

  // 모달 내 '동의' 버튼 클릭 시 폼 값(체크박스) 업데이트
  const handleTermsAgree = (alias) => {
    if (alias === 'terms-and-conditions') setValue('termsReq1', true, { shouldValidate: true });
    else if (alias === 'private-policy') setValue('termsReq2', true, { shouldValidate: true });
    else if (alias === 'advertising-consents')
      setValue('termsOpt1', true, { shouldValidate: true });
    else if (alias === 'marketing-consents') setValue('termsOpt2', true, { shouldValidate: true });

    closeTermsModal();
  };

  // 하위 체크박스 4개가 모두 선택되었을 때 '전체 동의' 자동 연동
  useEffect(() => {
    const isAllChecked = termsReq1 && termsReq2 && termsOpt1 && termsOpt2;
    if (termsAll !== isAllChecked) {
      setValue('termsAll', isAllChecked);
    }
  }, [termsReq1, termsReq2, termsOpt1, termsOpt2, termsAll, setValue]);

  const renderTermItem = (labelId, targetId, alias, isBold = false) => {
    const fullText = intl.formatMessage({ id: labelId });
    const target = intl.formatMessage({ id: targetId });
    const parts = typeof fullText === 'string' ? fullText.split(target) : [];

    return (
      <SignupTermHoverContainer
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          openTermsModal(alias);
        }}
      >
        <SignupTermsText isBold={isBold}>
          {parts.length === 2 ? (
            <>
              {parts[0]}
              <span className="underline-target">{target}</span>
              {parts[1]}
            </>
          ) : (
            <span className="underline-target">{fullText}</span>
          )}
        </SignupTermsText>
      </SignupTermHoverContainer>
    );
  };

  return (
    <>
      <SignupTermsBox>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Controller
            name="termsAll"
            control={control}
            render={({ field }) => (
              <SignupStyledFormControlLabel
                control={
                  <SignupStyledAllCheckbox
                    {...field}
                    checked={field.value}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      field.onChange(checked);
                      setValue('termsReq1', checked, { shouldValidate: true });
                      setValue('termsReq2', checked, { shouldValidate: true });
                      setValue('termsOpt1', checked, { shouldValidate: true });
                      setValue('termsOpt2', checked, { shouldValidate: true });
                    }}
                  />
                }
                label={
                  <SignupTermsAllText>
                    {intl.formatMessage({ id: 'lang-terms-agree-all' })}
                  </SignupTermsAllText>
                }
              />
            )}
          />
        </div>

        <SignupTermsAllDivider />

        <Controller
          name="termsReq1"
          control={control}
          render={({ field }) => (
            <SignupStyledFormControlLabel
              control={<SignupStyledCheckbox {...field} checked={field.value} />}
              label={renderTermItem(
                'lang-terms-conditions-label',
                'lang-terms-conditions-target',
                'terms-and-conditions',
                true
              )}
            />
          )}
        />
        <Controller
          name="termsReq2"
          control={control}
          render={({ field }) => (
            <SignupStyledFormControlLabel
              control={<SignupStyledCheckbox {...field} checked={field.value} />}
              label={renderTermItem(
                'lang-privacy-policy-label',
                'lang-privacy-policy-target',
                'private-policy',
                true
              )}
            />
          )}
        />
        <Controller
          name="termsOpt1"
          control={control}
          render={({ field }) => (
            <SignupStyledFormControlLabel
              control={<SignupStyledCheckbox {...field} checked={field.value} />}
              label={renderTermItem(
                'lang-mail-opt-label',
                'lang-mail-opt-target',
                'advertising-consents'
              )}
            />
          )}
        />
        <Controller
          name="termsOpt2"
          control={control}
          render={({ field }) => (
            <SignupStyledFormControlLabel
              control={<SignupStyledCheckbox {...field} checked={field.value} />}
              label={renderTermItem(
                'lang-mail-use-label',
                'lang-mail-use-target',
                'marketing-consents'
              )}
            />
          )}
        />
      </SignupTermsBox>

      <div style={{ minHeight: '1.8rem', marginTop: '4px' }}>
        {errors.termsReq1 && errors.termsReq2 ? (
          <span style={{ color: '#d32f2f', fontSize: '1.2rem', display: 'block' }}>
            {errors.termsReq1.message}
          </span>
        ) : errors.termsReq1 ? (
          <span style={{ color: '#d32f2f', fontSize: '1.2rem', display: 'block' }}>
            {errors.termsReq1.message}
          </span>
        ) : errors.termsReq2 ? (
          <span style={{ color: '#d32f2f', fontSize: '1.2rem', display: 'block' }}>
            {errors.termsReq2.message}
          </span>
        ) : null}
      </div>

      <InquiryTermsModal
        isOpen={termsModalConfig.isOpen}
        onClose={closeTermsModal}
        alias={termsModalConfig.alias}
        onAgree={() => handleTermsAgree(termsModalConfig.alias)}
      />
    </>
  );
};

export default InquiryTerms;
